import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { strategyApi, type VapiCall } from '@/modules/strategy/api/strategyApi';
import { agencyKeys } from '../../queryKeys';
import styles from './VapiCallsCard.module.css';

const CALL_STATE_LABEL: Record<string, string> = {
  scheduling: 'Scheduled',
  dialing: 'Dialing',
  in_progress: 'In Progress',
  processing: 'Processing',
  ended: 'Ended',
  no_answer: 'No Answer',
  voicemail: 'Voicemail',
  wrong_party: 'Wrong Party',
  cancelled: 'Cancelled',
  failed: 'Failed',
  captured_complete: 'Captured',
  captured_partial: 'Partial',
  not_captured: 'Not Captured',
  confirmed: 'Confirmed',
};

const CALL_STATE_VARIANT: Record<string, 'success' | 'warning' | 'neutral' | 'error'> = {
  scheduling: 'neutral',
  dialing: 'warning',
  in_progress: 'warning',
  processing: 'warning',
  ended: 'neutral',
  no_answer: 'warning',
  voicemail: 'warning',
  wrong_party: 'warning',
  cancelled: 'neutral',
  failed: 'error',
  captured_complete: 'success',
  captured_partial: 'warning',
  not_captured: 'neutral',
  confirmed: 'success',
};

const CAPTURE_LABEL: Record<string, string> = {
  complete: 'All fields captured',
  partial: 'Partial capture',
  none: 'Nothing captured',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function VapiCallRow({ call, practiceId }: { call: VapiCall; practiceId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const reprocessMutation = useMutation({
    mutationFn: () => strategyApi.reprocessVapiCall(call.vapiCallId),
    onSuccess: () => {
      toast.success('Call re-processed successfully.');
      setError(null);
      queryClient.invalidateQueries({ queryKey: agencyKeys.vapiCalls(practiceId) });
    },
    onError: (err: unknown) => {
      setError((err as { message?: string }).message ?? 'Re-process failed.');
    },
  });

  const state = call.callState;
  // Re-processing re-applies the answers stored from the call's report; it
  // isn't possible without them, or once the owner has confirmed the answers.
  const canReprocess =
    call.hasStoredAnswers &&
    !call.confirmedAt &&
    (state === 'captured_complete' ||
      state === 'captured_partial' ||
      state === 'not_captured' ||
      state === 'failed');
  const reprocessHint = call.confirmedAt
    ? 'Already confirmed by the owner'
    : !call.hasStoredAnswers
      ? 'No stored answers for this call'
      : undefined;

  return (
    <div className={styles.vapiCallEntry}>
      <div className={styles.vapiRow}>
        <div className={styles.vapiLeft}>
          <div className={styles.vapiMeta}>
            <Badge variant={CALL_STATE_VARIANT[state ?? ''] ?? 'neutral'} size="sm">
              {CALL_STATE_LABEL[state ?? ''] ?? state ?? 'Unknown'}
            </Badge>
            <span className={styles.vapiLocation}>{call.location.name}</span>
            {call.attempts > 1 && (
              <span className={styles.vapiAttempts}>attempt {call.attempts}</span>
            )}
          </div>
          <div className={styles.vapiDetails}>
            {state === 'scheduling' && call.scheduledAt ? (
              <span>Scheduled for {formatDateTime(call.scheduledAt)}</span>
            ) : (
              <span>{formatDateTime(call.processedAt)}</span>
            )}
            {call.captureLevel && (
              <span>· {CAPTURE_LABEL[call.captureLevel] ?? call.captureLevel}</span>
            )}
            {call.confirmedAt && <span>· Confirmed {formatDateTime(call.confirmedAt)}</span>}
            {call.endedReason && <span>· {call.endedReason}</span>}
            {call.strategyConfidence && <span>· Confidence: {call.strategyConfidence}</span>}
            {call.contactRole && <span>· {call.contactRole}</span>}
            {call.missingFields.length > 0 && (
              <span>· Missing: {call.missingFields.join(', ')}</span>
            )}
            {call.rescheduleNote && <span>· Note: {call.rescheduleNote}</span>}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={!canReprocess || reprocessMutation.isPending}
          title={reprocessHint}
          loading={reprocessMutation.isPending}
          onClick={() => reprocessMutation.mutate()}
        >
          Re-process
        </Button>
      </div>
      {error && <div className={styles.vapiError}>{error}</div>}
    </div>
  );
}

export function VapiCallsCard({ practiceId }: { practiceId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: agencyKeys.vapiCalls(practiceId),
    queryFn: () => strategyApi.getVapiCalls(practiceId),
  });

  const calls = data?.data ?? [];

  return (
    <Card title={`Strategy Intake Calls${calls.length > 0 ? ` (${calls.length})` : ''}`}>
      {isLoading ? (
        <div className={styles.vapiLoading}>
          <Spinner size="sm" />
        </div>
      ) : calls.length === 0 ? (
        <p className={styles.vapiEmpty}>No intake calls on record.</p>
      ) : (
        <div className={styles.vapiList}>
          {calls.map((call) => (
            <VapiCallRow key={call.id} call={call} practiceId={practiceId} />
          ))}
        </div>
      )}
    </Card>
  );
}
